import React, { useState, useEffect } from 'react';
import { B } from '../../constants';
import { borderRadius } from '../../designTokens';

const spinAnim = '@keyframes ls-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }';
const shimmerAnim = '@keyframes ls-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }';
const pulseAnim = '@keyframes ls-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }';
const waveAnim = '@keyframes ls-wave { 0%,100%{transform:scaleY(0.4)} 50%{transform:scaleY(1)} }';
const bounceAnim = '@keyframes ls-bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }';
const progressAnim = '@keyframes ls-progress { from{width:0%} to{width:100%} }';

function ensureKeyframes(css) {
  if (typeof document === 'undefined') return;
  const id = `ls-${css.slice(0, 20)}`;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

const sizeMap = {
  sm: { spinnerSize: 24, skeletonH: 14, skeletonW: 80, gap: 12, titleSize: 16, descSize: 12 },
  md: { spinnerSize: 44, skeletonH: 20, skeletonW: 120, gap: 16, titleSize: 20, descSize: 14 },
  lg: { spinnerSize: 64, skeletonH: 28, skeletonW: 160, gap: 20, titleSize: 24, descSize: 16 },
};

const skeletonSizeMap = {
  sm: { h: 14, w: 80, r: 'xs', avatar: 32 },
  md: { h: 20, w: 120, r: 'sm', avatar: 44 },
  lg: { h: 28, w: 160, r: 'md', avatar: 56 },
};

const speedMap = { slow: 2, normal: 1, fast: 0.5 };

function getReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(getReducedMotion);
  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const handler = (e) => setReduced(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch { return undefined; }
  }, []);
  return reduced;
}

function useAnimStyles(animationType, speed, reduced) {
  const mult = speedMap[speed] || 1;
  const d = reduced ? 0 : 1.5 / mult;

  useEffect(() => {
    if (reduced) return;
    if (animationType === 'shimmer') ensureKeyframes(shimmerAnim);
    else if (animationType === 'pulse') ensureKeyframes(pulseAnim);
    else if (animationType === 'wave') ensureKeyframes(waveAnim);
  }, [animationType, reduced]);

  if (reduced) return { animation: 'none', background: '#e5e7eb' };

  if (animationType === 'shimmer') {
    return {
      background: 'linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%)',
      backgroundSize: '200% 100%',
      animation: `ls-shimmer ${d}s ease infinite`,
    };
  }
  if (animationType === 'pulse') {
    return {
      background: '#e5e7eb',
      animation: `ls-pulse ${d}s ease infinite`,
    };
  }
  return {
    background: '#e5e7eb',
    animation: `ls-pulse ${d}s ease infinite`,
  };
}

function Spinner({ size: sz, color, speed, reduced }) {
  const s = sizeMap[sz] || sizeMap.md;
  const spinnerSize = typeof sz === 'number' ? sz : s.spinnerSize;
  const mult = speedMap[speed] || 1;
  const d = reduced ? 0 : 0.8 / mult;

  useEffect(() => { if (!reduced) ensureKeyframes(spinAnim); }, [reduced]);

  return (
    <div
      role="presentation"
      style={{
        width: spinnerSize, height: spinnerSize,
        borderRadius: '50%',
        border: `3px solid ${B.sand}`,
        borderTopColor: color || B.forest,
        animation: reduced ? 'none' : `ls-spin ${d}s linear infinite`,
      }}
    />
  );
}

function SkeletonAvatar({ size: sz, color, highlightColor, speed, radius: rad, reduced }) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles('shimmer', speed, reduced);
  const bg = reduced ? (color || '#e5e7eb') : undefined;

  return (
    <div
      aria-hidden="true"
      style={{
        width: ss.avatar, height: ss.avatar,
        borderRadius: rad ? (borderRadius[rad] || rad) : '50%',
        flexShrink: 0,
        ...anim,
        ...(color && !reduced ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}),
        ...(bg ? { background: bg } : {}),
      }}
    />
  );
}

function SkeletonText({ lines = 3, width, size: sz, color, highlightColor, speed, radius: rad, reduced, lastWidth = '60%' }) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles('shimmer', speed, reduced);
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.xs;
  const bg = reduced ? (color || '#e5e7eb') : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            height: ss.h,
            width: i === lines - 1 ? lastWidth : (width || `${ss.w}px`),
            borderRadius: r,
            ...anim,
            ...(color && !reduced ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}),
            ...(bg ? { background: bg } : {}),
          }}
        />
      ))}
    </div>
  );
}

function SkeletonImage({ height, size: sz, color, highlightColor, speed, radius: rad, reduced, aspectRatio }) {
  const anim = useAnimStyles('shimmer', speed, reduced);
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.md;
  const h = height || (sz === 'sm' ? 120 : sz === 'lg' ? 300 : 200);
  const bg = reduced ? (color || '#e5e7eb') : undefined;

  return (
    <div
      aria-hidden="true"
      style={{
        width: '100%',
        height: aspectRatio ? undefined : h,
        aspectRatio,
        borderRadius: r,
        flexShrink: 0,
        ...anim,
        ...(color && !reduced ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}),
        ...(bg ? { background: bg } : {}),
      }}
    />
  );
}

function SkeletonList({ rows = 3, size: sz, color, highlightColor, speed, radius: rad, reduced, avatar }) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles('shimmer', speed, reduced);
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.xs;
  const bg = reduced ? (color || '#e5e7eb') : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {avatar !== false && (
            <div
              style={{
                width: ss.avatar, height: ss.avatar,
                borderRadius: '50%', flexShrink: 0,
                ...anim,
                ...(color && !reduced ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}),
                ...(bg ? { background: bg } : {}),
              }}
            />
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ height: ss.h, width: '50%', borderRadius: r, ...anim, ...(color ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}), ...(bg ? { background: bg } : {}) }} />
            <div style={{ height: ss.h * 0.8, width: '80%', borderRadius: r, ...anim, ...(color ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}), ...(bg ? { background: bg } : {}) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonTable({ rows = 5, cols = 4, size: sz, color, highlightColor, speed, radius: rad, reduced }) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles('shimmer', speed, reduced);
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.xs;
  const bg = reduced ? (color || '#e5e7eb') : undefined;
  const colWidths = Array.from({ length: cols }, () => `${60 + Math.random() * 30}%`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
      {Array.from({ length: rows }).map((_, ri) => (
        <div key={ri} style={{ display: 'flex', gap: 12, padding: '10px 0' }}>
          {colWidths.map((cw, ci) => (
            <div key={ci} style={{ flex: 1 }}>
              <div style={{ height: ss.h, width: cw, borderRadius: r, ...anim, ...(color ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}), ...(bg ? { background: bg } : {}) }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function SkeletonCard({ size: sz, color, highlightColor, speed, radius: rad, reduced, height: cardH }) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles('shimmer', speed, reduced);
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.xl;
  const bg = reduced ? (color || '#e5e7eb') : undefined;
  const imgH = cardH || (sz === 'sm' ? 140 : sz === 'lg' ? 280 : 200);
  const bgGrad = color && !reduced
    ? `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`
    : 'linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%)';

  return (
    <div style={{ borderRadius: r, overflow: 'hidden', background: '#fafafa', width: '100%' }} aria-hidden="true">
      <div style={{
        height: imgH,
        background: bgGrad,
        backgroundSize: reduced ? undefined : '200% 100%',
        animation: reduced ? 'none' : 'ls-shimmer 1.5s ease infinite',
      }} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ height: ss.h, width: '70%', borderRadius: borderRadius.xs, ...anim, ...(bg ? { background: bg } : {}), ...(color && !reduced ? { background: bgGrad, backgroundSize: '200% 100%' } : {}) }} />
        <div style={{ height: ss.h * 0.7, width: '90%', borderRadius: borderRadius.xs, ...anim, ...(bg ? { background: bg } : {}), ...(color && !reduced ? { background: bgGrad, backgroundSize: '200% 100%' } : {}) }} />
      </div>
    </div>
  );
}

function Dots({ color, speed, reduced }) {
  const mult = speedMap[speed] || 1;
  const d = reduced ? 0 : 0.6 / mult;

  useEffect(() => { if (!reduced) ensureKeyframes(bounceAnim); }, [reduced]);

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} role="presentation" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 10, height: 10,
            borderRadius: '50%',
            background: color || B.forest,
            animation: reduced ? 'none' : `ls-bounce ${d}s ease ${i * 0.12}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function Bars({ color, speed, reduced }) {
  const mult = speedMap[speed] || 1;
  const d = reduced ? 0 : 0.6 / mult;

  useEffect(() => { if (!reduced) ensureKeyframes(waveAnim); }, [reduced]);

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: 30 }} role="presentation" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            width: 6, height: 30,
            borderRadius: 3,
            background: color || B.forest,
            transformOrigin: 'bottom',
            animation: reduced ? 'none' : `ls-wave ${d}s ease ${i * 0.1}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function LinearProgress({ value, color, speed, reduced }) {
  const mult = speedMap[speed] || 1;
  const d = reduced ? 0 : 1 / mult;
  const isIndeterminate = value === undefined || value === null;

  useEffect(() => { if (!reduced) ensureKeyframes(progressAnim); }, [reduced]);

  return (
    <div
      role="progressbar"
      aria-valuenow={isIndeterminate ? undefined : value}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        width: '100%', height: 8,
        borderRadius: 4,
        background: '#e5e7eb',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: isIndeterminate ? '40%' : `${Math.max(0, Math.min(100, value))}%`,
          borderRadius: 4,
          background: color || B.forest,
          animation: reduced ? 'none' : (isIndeterminate ? `ls-progress ${d}s ease infinite` : undefined),
        }}
      />
    </div>
  );
}

function Skeleton({
  width = '100%',
  height,
  size: sz,
  color,
  highlightColor,
  speed,
  radius: rad,
  variant = 'shimmer',
  reduced,
  style,
}) {
  const ss = skeletonSizeMap[sz] || skeletonSizeMap.md;
  const anim = useAnimStyles(variant, speed, reduced);
  const h = height || ss.h;
  const r = rad ? (borderRadius[rad] || rad) : borderRadius.sm;
  const bg = reduced ? (color || '#e5e7eb') : undefined;

  return (
    <div
      aria-hidden="true"
      style={{
        width, height: h,
        borderRadius: r,
        ...anim,
        ...(color && !reduced ? { background: `linear-gradient(90deg, ${color} 25%, ${highlightColor || '#f5f5f5'} 50%, ${color} 75%)`, backgroundSize: '200% 100%' } : {}),
        ...(bg ? { background: bg } : {}),
        ...style,
      }}
    />
  );
}

export default function LoadingState({
  variant = 'spinner',
  message,
  size = 'md',
  color,
  highlightColor,
  speed = 'normal',
  animationType = 'shimmer',
  spinnerColor,
  progressValue,
  progressColor,
  spinnerType,
  skeletonCount,
  skeletonLayout,
  style,
  ...props
}) {
  const reduced = useReducedMotion();

  if (variant === 'skeleton') {
    if (skeletonCount && skeletonCount > 1) {
      return (
        <div
          role="status"
          aria-label={message || 'Loading'}
          aria-busy="true"
          style={{
            display: 'flex', flexDirection: skeletonLayout === 'horizontal' ? 'row' : 'column',
            gap: sizeMap[size]?.gap || 16,
            width: '100%',
            ...style,
          }}
        >
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <SkeletonCard key={i} size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} />
          ))}
        </div>
      );
    }
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonCard size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} />
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }} role="status" aria-label={message || 'Loading'} aria-busy="true">
        <Spinner size={16} color={spinnerColor} speed={speed} reduced={reduced} />
      </span>
    );
  }

  if (variant === 'dots') {
    return (
      <div
        role="status"
        aria-label={message || 'Loading'}
        aria-busy="true"
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: sizeMap[size]?.gap || 16,
          minHeight: '60vh',
          ...style,
        }}
      >
        <Dots color={spinnerColor || color} speed={speed} reduced={reduced} />
        {message && <Message msg={message} />}
      </div>
    );
  }

  if (variant === 'bars') {
    return (
      <div
        role="status"
        aria-label={message || 'Loading'}
        aria-busy="true"
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: sizeMap[size]?.gap || 16,
          minHeight: '60vh',
          ...style,
        }}
      >
        <Bars color={spinnerColor || color} speed={speed} reduced={reduced} />
        {message && <Message msg={message} />}
      </div>
    );
  }

  if (variant === 'progress') {
    return (
      <div
        role="status"
        aria-label={message || 'Loading'}
        aria-busy="true"
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: sizeMap[size]?.gap || 16,
          padding: 20, width: '100%', maxWidth: 400,
          ...style,
        }}
      >
        <LinearProgress value={progressValue} color={progressColor || spinnerColor || color} speed={speed} reduced={reduced} />
        {message && <Message msg={message} />}
      </div>
    );
  }

  if (variant === 'avatar') {
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonAvatar size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} />
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonList size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} rows={skeletonCount || 3} />
      </div>
    );
  }

  if (variant === 'text') {
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonText size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} lines={skeletonCount || 3} />
      </div>
    );
  }

  if (variant === 'image') {
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonImage size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} />
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div role="status" aria-label={message || 'Loading'} aria-busy="true" style={{ width: '100%', ...style }}>
        <SkeletonTable size={size} color={color} highlightColor={highlightColor} speed={speed} reduced={reduced} rows={skeletonCount || 5} />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-label={message || 'Loading'}
      aria-busy="true"
      style={{
        minHeight: '60vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: sizeMap[size]?.gap || 16,
        ...style,
      }}
    >
      <Spinner size={sizeMap[size]?.spinnerSize} color={spinnerColor} speed={speed} reduced={reduced} />
      {message && <Message msg={message} />}
    </div>
  );
}

function Message({ msg }) {
  return (
    <p style={{
      color: B.muted, fontFamily: "'Montserrat',sans-serif",
      fontSize: 14, margin: 0, textAlign: 'center',
    }}>
      {msg}
    </p>
  );
}

export {
  Skeleton,
  SkeletonCard,
  SkeletonAvatar,
  SkeletonText,
  SkeletonImage,
  SkeletonList,
  SkeletonTable,
  Spinner,
  Dots,
  Bars,
  LinearProgress,
};
